/**
 * Revell Podcast & News Hub – JavaScript
 * Audio Player + View Tracking + Stats Dashboard
 */

(function () {
  'use strict';

  /* =========================================
     VIEW TRACKER
     Zählt Aufrufe per Artikel-Handle
     Speichert in localStorage (pro Browser)
     Verhindert Mehrfachzählung (1x / Session)
  ========================================= */
  const ViewTracker = {
    STORAGE_KEY: 'revell_podcast_views',
    SESSION_KEY: 'revell_podcast_session',

    getAll() {
      try {
        return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      } catch {
        return {};
      }
    },

    saveAll(data) {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      } catch (_) {}
    },

    getSessionViewed() {
      try {
        return JSON.parse(sessionStorage.getItem(this.SESSION_KEY) || '[]');
      } catch {
        return [];
      }
    },

    markSessionViewed(handle) {
      try {
        const viewed = this.getSessionViewed();
        if (!viewed.includes(handle)) {
          viewed.push(handle);
          sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(viewed));
        }
      } catch (_) {}
    },

    track(handle, title, type) {
      if (!handle) return;

      const sessionViewed = this.getSessionViewed();
      if (sessionViewed.includes(handle)) return;

      const all = this.getAll();
      const now = new Date().toISOString();

      if (!all[handle]) {
        all[handle] = { count: 0, title, type, first: now, last: now };
      }

      all[handle].count += 1;
      all[handle].last = now;
      all[handle].title = title;
      all[handle].type = type;

      this.saveAll(all);
      this.markSessionViewed(handle);

      this.updateDisplayedCounts(handle, all[handle].count);
    },

    getCount(handle) {
      const all = this.getAll();
      return all[handle] ? all[handle].count : 0;
    },

    updateDisplayedCounts(handle, count) {
      document.querySelectorAll(`[data-view-handle="${handle}"]`).forEach((el) => {
        el.textContent = count.toLocaleString('de-DE');
      });
    },

    initDisplayCounts() {
      document.querySelectorAll('[data-view-handle]').forEach((el) => {
        const handle = el.dataset.viewHandle;
        const count = this.getCount(handle);
        el.textContent = count.toLocaleString('de-DE');
      });
    },

    getAllStats() {
      const all = this.getAll();
      return Object.entries(all)
        .map(([handle, data]) => ({ handle, ...data }))
        .sort((a, b) => b.count - a.count);
    },
  };

  /* =========================================
     AUDIO PLAYER
  ========================================= */
  class PodcastAudioPlayer {
    constructor(container) {
      this.container = container;
      this.audioSrc = container.dataset.audioSrc;
      this.audio = new Audio();
      this.isPlaying = false;
      this.speeds = [1, 1.25, 1.5, 1.75, 2];
      this.speedIndex = 0;

      this.bindElements();
      this.bindEvents();
      this.loadAudio();
    }

    bindElements() {
      this.playBtn = this.container.querySelector('.podcast-player__btn--play');
      this.playIcon = this.container.querySelector('.podcast-player__play-icon');
      this.pauseIcon = this.container.querySelector('.podcast-player__pause-icon');
      this.rewindBtn = this.container.querySelector('.podcast-player__btn--rewind');
      this.forwardBtn = this.container.querySelector('.podcast-player__btn--forward');
      this.speedBtn = this.container.querySelector('.podcast-player__speed');
      this.progressWrap = this.container.querySelector('.podcast-player__progress-wrap');
      this.progressFill = this.container.querySelector('.podcast-player__progress-fill');
      this.progressThumb = this.container.querySelector('.podcast-player__progress-thumb');
      this.timeCurrentEl = this.container.querySelector('.podcast-player__time-current');
      this.timeTotalEl = this.container.querySelector('.podcast-player__time-total');
      this.volumeSlider = this.container.querySelector('.podcast-player__volume-slider');
    }

    loadAudio() {
      if (!this.audioSrc) return;
      this.audio.src = this.audioSrc;
      this.audio.preload = 'metadata';
    }

    bindEvents() {
      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => this.togglePlay());
      }
      if (this.rewindBtn) {
        this.rewindBtn.addEventListener('click', () => this.skip(-15));
      }
      if (this.forwardBtn) {
        this.forwardBtn.addEventListener('click', () => this.skip(30));
      }
      if (this.speedBtn) {
        this.speedBtn.addEventListener('click', () => this.cycleSpeed());
      }
      if (this.progressWrap) {
        this.progressWrap.addEventListener('click', (e) => this.seek(e));
        this.progressWrap.addEventListener('touchstart', (e) => this.seek(e.touches[0]), { passive: true });
      }
      if (this.volumeSlider) {
        this.volumeSlider.addEventListener('input', () => {
          this.audio.volume = parseFloat(this.volumeSlider.value);
        });
      }

      this.audio.addEventListener('timeupdate', () => this.onTimeUpdate());
      this.audio.addEventListener('loadedmetadata', () => this.onMetadata());
      this.audio.addEventListener('ended', () => this.onEnded());
      this.audio.addEventListener('error', () => this.onError());
    }

    togglePlay() {
      if (this.audio.paused) {
        this.audio.play().catch(() => {});
        this.setPlayState(true);
      } else {
        this.audio.pause();
        this.setPlayState(false);
      }
    }

    setPlayState(playing) {
      this.isPlaying = playing;
      if (this.playIcon) this.playIcon.classList.toggle('podcast-player__btn--hide', playing);
      if (this.pauseIcon) this.pauseIcon.classList.toggle('podcast-player__btn--hide', !playing);
    }

    skip(seconds) {
      this.audio.currentTime = Math.max(0, Math.min(this.audio.currentTime + seconds, this.audio.duration || 0));
    }

    seek(e) {
      if (!this.audio.duration) return;
      const rect = this.progressWrap.getBoundingClientRect();
      const x = (e.clientX || e.pageX) - rect.left;
      const ratio = Math.max(0, Math.min(x / rect.width, 1));
      this.audio.currentTime = ratio * this.audio.duration;
    }

    cycleSpeed() {
      this.speedIndex = (this.speedIndex + 1) % this.speeds.length;
      const speed = this.speeds[this.speedIndex];
      this.audio.playbackRate = speed;
      if (this.speedBtn) this.speedBtn.textContent = speed + '×';
    }

    onTimeUpdate() {
      const current = this.audio.currentTime || 0;
      const total = this.audio.duration || 0;
      const ratio = total ? (current / total) * 100 : 0;

      if (this.progressFill) this.progressFill.style.width = ratio + '%';
      if (this.progressThumb) this.progressThumb.style.left = ratio + '%';
      if (this.timeCurrentEl) this.timeCurrentEl.textContent = this.formatTime(current);
    }

    onMetadata() {
      if (this.timeTotalEl) this.timeTotalEl.textContent = this.formatTime(this.audio.duration);
    }

    onEnded() {
      this.setPlayState(false);
      if (this.progressFill) this.progressFill.style.width = '0%';
      if (this.progressThumb) this.progressThumb.style.left = '0%';
      this.audio.currentTime = 0;
    }

    onError() {
      console.warn('[PodcastPlayer] Audio konnte nicht geladen werden:', this.audioSrc);
    }

    formatTime(seconds) {
      if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}:${s.toString().padStart(2, '0')}`;
    }
  }


  /* =========================================
     FILTER TABS (Podcast / News)
  ========================================= */
  function initFilterTabs() {
    const tabs = document.querySelectorAll('.podcast-filter-btn');
    const cards = document.querySelectorAll('.podcast-card-wrap, .news-flash-card-wrap');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');

        const filter = tab.dataset.filter;
        let visibleCount = 0;

        cards.forEach((card) => {
          const type = card.dataset.type || 'all';
          const show = filter === 'all' || type === filter;
          card.style.display = show ? '' : 'none';
          if (show) visibleCount++;
        });

        const emptyEl = document.querySelector('.podcast-hub__empty');
        if (emptyEl) emptyEl.style.display = visibleCount === 0 ? 'block' : 'none';
      });
    });
  }

  /* =========================================
     STATS DASHBOARD RENDERER
  ========================================= */
  function initStatsDashboard() {
    const dashboardEl = document.getElementById('podcast-stats-dashboard');
    if (!dashboardEl) return;

    const stats = ViewTracker.getAllStats();

    // KPI-Zahlen befüllen
    const totalViewsEl = document.getElementById('stats-total-views');
    const totalArticlesEl = document.getElementById('stats-total-articles');
    const podcastViewsEl = document.getElementById('stats-podcast-views');
    const newsViewsEl = document.getElementById('stats-news-views');

    const totalViews = stats.reduce((s, i) => s + i.count, 0);
    const podcastViews = stats.filter((i) => i.type === 'podcast').reduce((s, i) => s + i.count, 0);
    const newsViews = stats.filter((i) => i.type !== 'podcast').reduce((s, i) => s + i.count, 0);

    if (totalViewsEl) totalViewsEl.textContent = totalViews.toLocaleString('de-DE');
    if (totalArticlesEl) totalArticlesEl.textContent = stats.length.toLocaleString('de-DE');
    if (podcastViewsEl) podcastViewsEl.textContent = podcastViews.toLocaleString('de-DE');
    if (newsViewsEl) newsViewsEl.textContent = newsViews.toLocaleString('de-DE');

    // Tabelle befüllen
    const tbody = document.getElementById('stats-table-body');
    if (!tbody) return;

    if (stats.length === 0) {
      tbody.innerHTML = `<tr><td colspan="5" style="text-align:center; padding: 40px; color: #677279;">
        Noch keine Aufrufe getrackt. Besuche einige Podcast- oder News-Artikel.
      </td></tr>`;
      return;
    }

    const maxCount = stats[0]?.count || 1;

    tbody.innerHTML = stats
      .map(
        (item, idx) => `
      <tr>
        <td style="color: #677279; font-size: 13px;">${idx + 1}</td>
        <td class="stats-table__title">
          <a href="/blogs/podcasts/${item.handle}" target="_blank">${item.title || item.handle}</a>
        </td>
        <td>
          <span class="stats-table__type-badge stats-table__type-badge--${item.type || 'news'}">
            ${typeLabel(item.type)}
          </span>
        </td>
        <td>
          <div class="stats-bar">
            <div class="stats-bar__track">
              <div class="stats-bar__fill" style="width: ${(item.count / maxCount) * 100}%"></div>
            </div>
            <span class="stats-bar__value">${item.count.toLocaleString('de-DE')}</span>
          </div>
        </td>
        <td style="font-size: 12px; color: #677279;">
          ${item.last ? new Date(item.last).toLocaleDateString('de-DE') : '–'}
        </td>
      </tr>
    `
      )
      .join('');

    // Animate bars
    setTimeout(() => {
      document.querySelectorAll('.stats-bar__fill').forEach((bar) => {
        bar.style.transition = 'width 0.8s ease';
      });
    }, 100);
  }

  function typeLabel(type) {
    const map = { podcast: 'Podcast', news: 'News', offer: 'Angebot' };
    return map[type] || type || 'Artikel';
  }

  /* =========================================
     INLINE CARD PLAYER
  ========================================= */
  const InlineCardPlayer = {
    init(el) {
      if (el._inlineAudio) { el._inlineAudio.pause(); }

      const audio = new Audio(el.dataset.audioSrc);
      el._inlineAudio = audio;

      const fill     = el.querySelector('.pci__progress-fill');
      const track    = el.querySelector('.pci__progress-wrap');
      const playBtn  = el.querySelector('.pci__btn--play');
      const playIcon = el.querySelector('.pci__play-icon');
      const pauseIcon= el.querySelector('.pci__pause-icon');
      const rewind   = el.querySelector('.pci__btn--rewind');
      const forward  = el.querySelector('.pci__btn--forward');
      const speedBtn = el.querySelector('.pci__speed');
      const current  = el.querySelector('.pci__current');
      const total    = el.querySelector('.pci__total');

      const speeds = [1, 1.25, 1.5, 1.75, 2];
      let speedIdx = 0;

      // Kapitel parsen: "0:30 | Label | https://... | Beschreibung" pro Zeile
      const chapters = (el.dataset.chapters || '')
        .split('\n')
        .map((line) => {
          const parts = line.split('|').map((s) => s.trim());
          if (parts.length < 2) return null;
          const [time, label, url, description] = parts;
          const [m, s2] = time.split(':').map(Number);
          const secs = (m || 0) * 60 + (s2 || 0);
          return { secs, label, url: url || '', description: description || '' };
        })
        .filter(Boolean)
        .sort((a, b) => a.secs - b.secs);

      const chaptersPlayerWrap = el.querySelector('.pci__chapters-player-wrap');
      const chaptersPlayer     = el.querySelector('.pci__chapters-player');
      const shownChapters  = new Set();

      const buildChapterItem = (c) => {
        const item = document.createElement('div');
        item.className = 'pci__chapter-item';
        const labelHtml = c.url
          ? `<a href="${c.url}" target="_blank" rel="noopener noreferrer">
               ${c.label}
               <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                 <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                 <polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/>
               </svg>
             </a>`
          : c.label;
        item.innerHTML = `
          <div class="pci__chapter-meta">
            <span class="pci__chapter-dot"></span>
            <span class="pci__chapter-time">${fmt(c.secs)}</span>
            <span class="pci__chapter-badge">Kapitel</span>
          </div>
          <div class="pci__chapter-label">${labelHtml}</div>
          ${c.description ? `<div class="pci__chapter-desc">${c.description}</div>` : ''}`;
        return item;
      };

      const showChapter = (idx) => {
        if (shownChapters.has(idx)) return;
        shownChapters.add(idx);
        const c = chapters[idx];

        if (chaptersPlayer) {
          if (chaptersPlayerWrap) chaptersPlayerWrap.style.display = '';
          chaptersPlayer.prepend(buildChapterItem(c));
        }
      };

      const fmt = (s) => isFinite(s) ? Math.floor(s/60) + ':' + String(Math.floor(s%60)).padStart(2,'0') : '0:00';
      const setPlay = (p) => { playIcon.style.display = p ? 'none' : ''; pauseIcon.style.display = p ? '' : 'none'; };

      audio.addEventListener('loadedmetadata', () => { if (total) total.textContent = fmt(audio.duration); });
      audio.addEventListener('timeupdate', () => {
        const r = audio.duration ? audio.currentTime / audio.duration * 100 : 0;
        if (fill) fill.style.width = r + '%';
        if (current) current.textContent = fmt(audio.currentTime);

        // Kapitel prüfen
        if (chapters.length > 0) {
          const t = audio.currentTime;
          for (let i = chapters.length - 1; i >= 0; i--) {
            if (t >= chapters[i].secs) {
              showChapter(i);
              break;
            }
          }
        }
      });
      audio.addEventListener('ended', () => { setPlay(false); audio.currentTime = 0; if (fill) fill.style.width = '0%'; });

      playBtn?.addEventListener('click', () => {
        if (audio.paused) { audio.play().then(() => setPlay(true)).catch(() => {}); }
        else { audio.pause(); setPlay(false); }
      });
      rewind?.addEventListener('click',  () => { audio.currentTime = Math.max(0, audio.currentTime - 15); });
      forward?.addEventListener('click', () => { audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + 30); });
      track?.addEventListener('click', (e) => {
        if (!audio.duration) return;
        const rect = track.getBoundingClientRect();
        audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
      });
      speedBtn?.addEventListener('click', () => {
        speedIdx = (speedIdx + 1) % speeds.length;
        audio.playbackRate = speeds[speedIdx];
        speedBtn.textContent = speeds[speedIdx] + '×';
      });

      audio.play().then(() => setPlay(true)).catch(() => {});
    },
  };

  /* =========================================
     BOTTOM PLAYER
     Globaler persistenter Player am Seitenrand
  ========================================= */
  const BottomPlayer = {
    el: null,
    audio: new Audio(),
    isPlaying: false,
    speeds: [1, 1.25, 1.5, 1.75, 2],
    speedIndex: 0,
    currentHandle: null,

    init() {
      this.el = document.getElementById('podcast-bottom-player');
      if (!this.el) return;

      this.playBtn    = document.getElementById('podcast-bp-play');
      this.playIcon   = this.el.querySelector('.podcast-bp__play-icon');
      this.pauseIcon  = this.el.querySelector('.podcast-bp__pause-icon');
      this.rewindBtn  = document.getElementById('podcast-bp-rewind');
      this.forwardBtn = document.getElementById('podcast-bp-forward');
      this.speedBtn   = document.getElementById('podcast-bp-speed');
      this.closeBtn   = document.getElementById('podcast-bp-close');
      this.track      = document.getElementById('podcast-bp-track');
      this.fill       = document.getElementById('podcast-bp-fill');
      this.currentEl  = document.getElementById('podcast-bp-current');
      this.totalEl    = document.getElementById('podcast-bp-total');
      this.titleEl    = document.getElementById('podcast-bp-title');
      this.episodeEl  = document.getElementById('podcast-bp-episode');
      this.coverEl    = document.getElementById('podcast-bp-cover');

      this.bindEvents();
    },

    bindEvents() {
      this.playBtn?.addEventListener('click',   () => this.togglePlay());
      this.rewindBtn?.addEventListener('click', () => this.skip(-15));
      this.forwardBtn?.addEventListener('click',() => this.skip(30));
      this.speedBtn?.addEventListener('click',  () => this.cycleSpeed());
      this.closeBtn?.addEventListener('click',  () => this.close());
      this.track?.addEventListener('click',     (e) => this.seek(e));

      this.audio.addEventListener('timeupdate',    () => this.onTimeUpdate());
      this.audio.addEventListener('loadedmetadata',() => this.onMetadata());
      this.audio.addEventListener('ended',         () => this.onEnded());
    },

    open(data) {
      // data: { src, title, episode, coverUrl, articleUrl, handle }
      const sameTrack = this.currentHandle && this.currentHandle === data.handle;

      if (!sameTrack) {
        this.audio.src = data.src;
        this.audio.load();
        this.currentHandle = data.handle;
        if (this.titleEl)   this.titleEl.textContent   = data.title   || '';
        if (this.episodeEl) this.episodeEl.textContent = data.episode ? 'Folge ' + data.episode : '';

        // Cover
        if (this.coverEl) {
          if (data.coverUrl) {
            this.coverEl.innerHTML = `<img src="${data.coverUrl}" alt="${data.title || ''}" loading="lazy">`;
          } else {
            this.coverEl.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>`;
          }
        }
        this.fill && (this.fill.style.width = '0%');
        this.currentEl && (this.currentEl.textContent = '0:00');
        this.totalEl   && (this.totalEl.textContent   = '–');
      }

      this.el.classList.add('is-visible');
      this.el.setAttribute('aria-hidden', 'false');
      this.audio.play().then(() => this.setPlayState(true)).catch(() => {});
    },

    close() {
      this.audio.pause();
      this.setPlayState(false);
      this.el.classList.remove('is-visible');
      this.el.setAttribute('aria-hidden', 'true');
    },

    togglePlay() {
      if (this.audio.paused) {
        this.audio.play().then(() => this.setPlayState(true)).catch(() => {});
      } else {
        this.audio.pause();
        this.setPlayState(false);
      }
    },

    setPlayState(playing) {
      this.isPlaying = playing;
      if (this.playIcon)  this.playIcon.style.display  = playing ? 'none'  : '';
      if (this.pauseIcon) this.pauseIcon.style.display = playing ? ''      : 'none';
    },

    skip(sec) {
      this.audio.currentTime = Math.max(0, Math.min(this.audio.currentTime + sec, this.audio.duration || 0));
    },

    seek(e) {
      if (!this.audio.duration) return;
      const rect  = this.track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min((e.clientX - rect.left) / rect.width, 1));
      this.audio.currentTime = ratio * this.audio.duration;
    },

    cycleSpeed() {
      this.speedIndex = (this.speedIndex + 1) % this.speeds.length;
      const s = this.speeds[this.speedIndex];
      this.audio.playbackRate = s;
      if (this.speedBtn) this.speedBtn.textContent = s + '×';
    },

    onTimeUpdate() {
      const cur   = this.audio.currentTime || 0;
      const total = this.audio.duration    || 0;
      if (this.fill)      this.fill.style.width       = total ? (cur / total * 100) + '%' : '0%';
      if (this.currentEl) this.currentEl.textContent  = this.fmt(cur);
    },

    onMetadata() {
      if (this.totalEl) this.totalEl.textContent = this.fmt(this.audio.duration);
    },

    onEnded() {
      this.setPlayState(false);
      if (this.fill)      this.fill.style.width      = '0%';
      if (this.currentEl) this.currentEl.textContent = '0:00';
      this.audio.currentTime = 0;
    },

    fmt(s) {
      if (!isFinite(s)) return '0:00';
      const m = Math.floor(s / 60);
      return m + ':' + String(Math.floor(s % 60)).padStart(2, '0');
    },
  };

  /* =========================================
     INIT
  ========================================= */
  function init() {
    // View-Counter für alle Artikel anzeigen
    ViewTracker.initDisplayCounts();

    // View tracken auf Artikel-Seite
    const articleEl = document.getElementById('podcast-article-data');
    if (articleEl) {
      const handle = articleEl.dataset.handle;
      const title = articleEl.dataset.title;
      const type = articleEl.dataset.type;
      if (handle) {
        ViewTracker.track(handle, title, type);
        // View-Zähler auf der aktuellen Seite aktualisieren
        const viewEl = document.querySelector('[data-view-current]');
        if (viewEl) {
          viewEl.textContent = ViewTracker.getCount(handle).toLocaleString('de-DE');
        }
      }
    }

    // Bottom Player initialisieren
    BottomPlayer.init();

    // Play-Button auf Karte → Inline-Player öffnen
    document.addEventListener('click', (e) => {
      const playBtn = e.target.closest('.podcast-card__play-trigger');
      if (!playBtn) return;
      e.preventDefault();
      e.stopPropagation();

      const card = playBtn.closest('.podcast-card');
      if (!card) return;

      const inlinePlayer = card.querySelector('.podcast-card__inline-player');
      if (!inlinePlayer) return;

      const isOpen = inlinePlayer.style.display !== 'none';

      // Alle anderen Inline-Player schließen & Audio stoppen
      document.querySelectorAll('.podcast-card__inline-player').forEach((p) => {
        if (p !== inlinePlayer) {
          p.style.display = 'none';
          if (p._inlineAudio) { p._inlineAudio.pause(); p._inlineAudio = null; }
          const pi = p.closest('.podcast-card');
          if (pi) pi.querySelector('.podcast-card__play-overlay')?.classList.remove('is-playing');
        }
      });

      if (isOpen) {
        // Schließen
        inlinePlayer.style.display = 'none';
        if (inlinePlayer._inlineAudio) { inlinePlayer._inlineAudio.pause(); inlinePlayer._inlineAudio = null; }
        playBtn.classList.remove('is-playing');
      } else {
        // Öffnen + Audio starten
        inlinePlayer.style.display = 'block';
        playBtn.classList.add('is-playing');
        InlineCardPlayer.init(inlinePlayer);

        // View tracken
        const handle = inlinePlayer.dataset.handle;
        const title  = inlinePlayer.dataset.title;
        if (handle) ViewTracker.track(handle, title, 'podcast');
      }
    });

    // Audio Player auf Artikel-Detail initialisieren
    document.querySelectorAll('.podcast-player[data-audio-src]').forEach((el) => {
      new PodcastAudioPlayer(el);
    });

    // Filter-Tabs
    initFilterTabs();

    // Stats Dashboard
    initStatsDashboard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
